import { useEffect, useRef, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { FaPause } from "react-icons/fa6";
import { MdSkipNext, MdSkipPrevious } from "react-icons/md";
import LoaderSpiner from "../../LoaderSpiner";
import styles from "./VoiceDemo.module.css";

const API_BASE_URL =
  "https://prod.toxboxai.com";
const VOICES_API = `${API_BASE_URL}/api/voice/get-voice-by-display-name`;

const languageMap = {
  EN: "English",
  ES: "Spanish",
  FR: "French",
  DE: "German",
  IT: "Italian",
  PT: "Portuguese",
  RU: "Russian",
  ZH: "Chinese",
  JA: "Japanese",
  KO: "Korean",
  AR: "Arabic",
  HI: "Hindi",
  // Expanded mappings
  BR: "Portuguese", // Likely refers to Brazilian Portuguese, often uses PT, but listed as BR for country
  BU: "Bulgarian", // Assuming BU is a typo or shorthand for BG (Bulgarian)
  CH: "Chinese", // Likely refers to ZH (Chinese), as CH is typically a country code for Switzerland
  CZ: "Czech", // Assuming CZ is a typo or shorthand for CS (Czech, though CS is deprecated; CZ is country code)
  DA: "Danish",
  DU: "Dutch", // Assuming DU is a typo or shorthand for NL (Dutch)
  FI: "Finnish",
  GE: "Georgian", // Assuming GE is a typo or shorthand for KA (Georgian)
  GR: "Greek", // Assuming GR is a typo or shorthand for EL (Greek)
  HU: "Hungarian",
  NO: "Norwegian",
  PO: "Polish", // Assuming PO is a typo or shorthand for PL (Polish)
  RO: "Romanian",
  RA: "Rassaion", // Unclear, possibly a typo; could be RA for Rajasthani, but not standard
  SL: "Slovenian", // Could also be Slovak (SK); assuming Slovenian
  SW: "Swahili",
  SP: "Spanish", // Assuming SP is a typo or shorthand for ES (Spanish)
  TA: "Tamil",
  TU: "Turkish", // Assuming TU is a typo or shorthand for TR (Turkish)
  UK: "Ukrainian",
  VI: "Vietnamese",
};

// Modified extractLanguage function
const extractLanguage = (name) => {
  const parts = name.split("-");
  if (parts.length > 1) {
    let code = parts[1].split(" ")[0].toUpperCase();
    return languageMap[code] || code; // Return mapped language or code if not mapped
  }
  return "English"; // Default to English instead of UNKNOWN
};

const VoiceDemo = () => {
  const [voices, setVoices] = useState([]);
  const [filteredVoices, setFilteredVoices] = useState([]);
  const [genders, setGenders] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedGender, setSelectedGender] = useState("All");
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Fetch voices on mount
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await fetch(VOICES_API);
        const data = await response.json();
        const processedData = data
          .filter((v) => v.gender && v.gender !== "UNKNOWN") // Filter out unknown genders
          .map((v) => ({
            ...v,
            language: extractLanguage(v.displayName),
          }));

        setVoices(processedData);

        const uniqueGenders = [
          "Genders",
          ...new Set(
            processedData.map((v) => v.gender).filter((g) => g !== "Unknown")
          ),
        ];
        setGenders(uniqueGenders);

        const langSet = new Set(processedData.map((v) => v.language));
        setLanguages(["Languages", ...Array.from(langSet).sort()]);

        setFilteredVoices(processedData);

        if (processedData.length > 0) {
          setSelectedDemo({
            ...processedData[0],
            name: processedData[0].displayName,
            age: "Adult",
            useCase: "Narration",
          });
        }
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    };
    fetchVoices();
  }, []);

  // Filter voices
  useEffect(() => {
    let filtered = voices;
    if (selectedGender !== "All") {
      filtered = filtered.filter((v) => v.gender === selectedGender);
    }
    if (selectedLanguage !== "All") {
      filtered = filtered.filter((v) => v.language === selectedLanguage);
    }
    setFilteredVoices(filtered);

    if (selectedDemo && !filtered.find((v) => v._id === selectedDemo._id)) {
      if (filtered.length > 0) {
        setSelectedDemo({
          ...filtered[0],
          name: filtered[0].displayName,
          age: "Adult",
          useCase: "Narration",
        });
      } else {
        setSelectedDemo(null);
      }
    }
  }, [selectedGender, selectedLanguage, voices]);

  // Handle audio events when selectedDemo changes
  useEffect(() => {
    if (selectedDemo && audioRef.current) {
      audioRef.current.src = `${API_BASE_URL}${selectedDemo.previewUrl}`;
      audioRef.current.load();
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
    }
  }, [selectedDemo]);

  // Add event listeners for audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [selectedDemo]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handlePrev = () => {
    if (filteredVoices.length === 0) return;
    const currentIndex = filteredVoices.findIndex(
      (v) => v._id === selectedDemo._id
    );
    const prevIndex =
      (currentIndex - 1 + filteredVoices.length) % filteredVoices.length;
    setSelectedDemo({
      ...filteredVoices[prevIndex],
      name: filteredVoices[prevIndex].displayName,
      age: "Adult",
      useCase: "Narration",
    });
  };

  const handleNext = () => {
    if (filteredVoices.length === 0) return;
    const currentIndex = filteredVoices.findIndex(
      (v) => v._id === selectedDemo._id
    );
    const nextIndex = (currentIndex + 1) % filteredVoices.length;
    setSelectedDemo({
      ...filteredVoices[nextIndex],
      name: filteredVoices[nextIndex].displayName,
      age: "Adult",
      useCase: "Narration",
    });
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (voices.length === 0) {
    <LoaderSpiner />;
  }

  if (!selectedDemo) {
    return <div>No voices available for the selected filters.</div>;
  }

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.heading}>Explore Voice Demos</h2>
      <div className={styles.filters}>
        <div>
          <select
            value={selectedGender}
            onChange={(e) => setSelectedGender(e.target.value)}
          >
            {genders.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            {languages.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={styles.container}>
        {/* Left Side */}
        <div className={styles.leftPanel}>
          <h3>{selectedDemo.name}</h3>
          <p>Press play and find your brand's voice</p>
          <div className={styles.details}>
            <p className={styles.det}>
              <strong>Age :</strong> {selectedDemo.age}
            </p>
            <p>
              <strong>Gender :</strong> {selectedDemo.gender}
            </p>
          </div>
          <div className={styles.audioControls}>
            <div className={styles.control}>
              <button className={styles.prev} onClick={handlePrev}>
                <MdSkipPrevious />
              </button>
              <button className={styles.playButton} onClick={togglePlay}>
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
              <button className={styles.next} onClick={handleNext}>
                <MdSkipNext />
              </button>
            </div>
            <div className={styles.progressBar}>
              <span>{formatTime(currentTime)}</span>
              <div
                className={styles.progress}
                style={{
                  width: `${duration > 0 ? (currentTime / duration) * 100 : 0
                    }%`,
                }}
              ></div>
              {/* <span>{formatTime(duration)}</span> */}
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className={styles.rightPanel}>
          {filteredVoices.length === 0 ? (
            <p>No voices match the selected filters.</p>
          ) : (
            filteredVoices.map((demo) => (
              <div
                key={demo._id}
                className={`${styles.demoItem} ${selectedDemo._id === demo._id ? styles.active : ""
                  }`}
                onClick={() =>
                  setSelectedDemo({
                    ...demo,
                    name: demo.displayName,
                    age: "Adult",
                    useCase: "Narration",
                  })
                }
              >
                <div className={styles.demoInfo}>
                  <span>{demo.displayName}</span>
                </div>
                <span className={styles.use}>{demo.gender}</span>
              </div>
            ))
          )}
        </div>
      </div>
      <audio ref={audioRef} preload="metadata" />
    </div>
  );
};

export default VoiceDemo;
